import { FC, useEffect, useMemo } from "react"
import {
  Link,
  Outlet,
  useLocation,
  useMatch,
  useNavigate
} from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../common/hooks"
import { Dropdown, PageHeader } from "../../common/ui"
import { checkIsBankAdmin } from "../../common/utils"
import {
  useFetchBrandQuery,
  useFetchBrandsQuery,
  useFetchLoanOfficerQuery
} from "../../redux/queries"
import {
  getMe,
  selectPasswordUpdated,
  selectProfileUpdated,
  selectUserDetails
} from "../../redux/slices/auth"
import { ChevronDown } from "lucide-react"

type Props = {
  title?: string
}

type SectionType = "settings" | "lo_profile" | "bank_billing"

interface ISection {
  key: SectionType
  name: string
  path: string
}

const SECTIONS: ISection[] = [
  {
    key: "settings",
    path: "/account",
    name: "My account settings"
  },
  {
    key: "lo_profile",
    path: "/account/lo-profile",
    name: "My loan officer profile"
  },
  {
    key: "bank_billing",
    path: "/account/billing",
    name: "Billing"
  }
]

const AccountPage: FC<Props> = ({ title = "Account" }) => {
  const dispatch = useAppDispatch()

  const passwordUpdated = useAppSelector(selectPasswordUpdated)
  const profileUpdated = useAppSelector(selectProfileUpdated)

  const user = useAppSelector(selectUserDetails)

  const { data: loProfile } = useFetchLoanOfficerQuery(user?.id ?? "", {
    skip: !user?.id
  })

  const isBankAdmin = checkIsBankAdmin(user?.roles || [])

  const { data: brands } = useFetchBrandsQuery({})

  const selectedBrand = useMemo(
    () => brands?.find(b => b.id === loProfile?.bank_nid),
    [brands, loProfile?.bank_nid]
  )
  const { data: apiBrand } = useFetchBrandQuery(loProfile?.bank_nid ?? "", {
    skip: !loProfile?.bank_nid || !!selectedBrand?.id
  })

  const brand = selectedBrand || apiBrand

  useEffect(() => {
    profileUpdated && dispatch(getMe())
  }, [dispatch, profileUpdated])

  let location = useLocation()

  const navigate = useNavigate()

  const match = useMatch("/account/billing")

  if (!user) return null

  if (!isBankAdmin && match) {
    navigate("/account")
  }

  return (
    <>
      <div className="p-6 pt-4 sm:pt-6 border-neutral-200">
        <PageHeader title={title} />
      </div>
      <div className="p-6 bg-primary-100 flex-grow">
        <div className="md:flex items-start gap-6">
          <div className="w-64 bg-white p-4 rounded-lg hidden md:block">
            {SECTIONS.map(({ key, path, name }) => {
              const isActive = location.pathname.endsWith(path)

              if (key === "bank_billing" && !isBankAdmin) return null
              return (
                <Link
                  key={key}
                  className={`block text-sm font-medium py-2 px-3 rounded-md cursor-pointer ${
                    isActive ? "bg-neutral-100" : "bg-white"
                  }`}
                  to={path}
                >
                  {name}
                </Link>
              )
            })}
          </div>

          <div className="flex md:hidden mb-4">
            <Dropdown
              className="ml-auto min-w-48"
              items={SECTIONS.map(({ key, path, name }) => ({
                name,
                onClick: () => navigate(path)
              }))}
              menuBtn={
                <div className="flex items-center justify-between w-full bg-white p-4 rounded-lg">
                  <span className="text-sm font-medium">
                    {SECTIONS.find(({ path }) =>
                      location.pathname.endsWith(path)
                    )?.name ?? "Billing"}
                  </span>
                  <ChevronDown size={20} />
                </div>
              }
            />
          </div>
          <div className="flex-1 rounded-lg bg-white">
            <Outlet
              context={{ user, passwordUpdated, profile: loProfile, brand }}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default AccountPage
